import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { memberApi } from '../api';
import PaymentSetup from './PaymentSetup';
import LoadingSpinner from '../shared/components/LoadingSpinner';

interface Invoice {
  id: string;
  amount: number;
  status: string;
  date: string;
  pdfUrl: string;
  hostedUrl: string;
}

interface PaymentMethod {
  id: string;
  type: string;
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  billing_details: {
    name: string;
    email: string;
  };
  created: number;
}

// Types for the subscription data
interface MemberSubscription {
  id: string;
  status: string;
  planId: string;
  planName: string;
  amount: number;
  startDate: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  lastPaymentDate: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  paymentMethod?: {
    id: string;
  };
}

// Types for the subscription data
interface MemberSubscription {
  id: string;
  status: string;
  planId: string;
  planName: string;
  amount: number;
  startDate: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  lastPaymentDate: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  paymentMethod?: {
    id: string;
  };
}

interface MemberSubscriptionProps {
  businessId: string;
  facilityId: string;
}

export default function NewMemberSubscription({ businessId, facilityId }: MemberSubscriptionProps) {
  const [subscription, setSubscription] = useState<MemberSubscription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'subscription' | 'payment' | 'setup'>('subscription');
  const [updatingPaymentMethod, setUpdatingPaymentMethod] = useState<string | null>(null);
  const [removingPaymentMethod, setRemovingPaymentMethod] = useState<string | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);
        const token = await currentUser.getIdToken();

        if (!businessId || !facilityId) {
          setError('No business or facility ID found');
          setLoading(false);
          return;
        }

        // Fetch subscription details using the API
        const subscriptionData = await memberApi.getSubscription(currentUser.uid, token, businessId, facilityId);
        setSubscription(subscriptionData as unknown as MemberSubscription);

        if (subscriptionData) {
          const invoicesResponse = await memberApi.getMemberInvoice(currentUser.uid, token, businessId, facilityId);
          setInvoices(invoicesResponse);
        }

        // Fetch payment methods
        const paymentMethodsResponse = await memberApi.getPaymentMethods(currentUser.uid, token, businessId, facilityId);
        setPaymentMethods(paymentMethodsResponse.data);

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An error occurred';
        if (errorMessage.includes('No subscription found')) {
          setError('You do not have an active subscription. Please contact your facility administrator.');
        } else {
          setError(errorMessage);
        }
        console.error('Error fetching subscription data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionData();
  }, [currentUser, businessId, facilityId]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    if (!currentUser) return;
    
    if (!businessId || !facilityId) {
      setError('No business or facility ID found');
      setLoading(false);
      return;
    }
    
    try {
      setUpdatingPaymentMethod(paymentMethodId);
      const token = await currentUser.getIdToken();
      const subscriptionData = await memberApi.getSubscription(currentUser.uid, token, businessId, facilityId);
      setSubscription(subscriptionData as unknown as MemberSubscription);
      
      const result = await memberApi.setDefaultPaymentMethod(currentUser.uid, paymentMethodId, token, businessId, facilityId);
      
      if (result.success) {
        if (!facilityId || !businessId) {
          setError('No business or facility ID found');
          setLoading(false);
          return;
        }
        // Refresh both subscription and payment methods data
        const [subscriptionData, paymentMethodsResponse] = await Promise.all([
          memberApi.getSubscription(currentUser.uid, token, businessId, facilityId),
          memberApi.getPaymentMethods(currentUser.uid, token, businessId, facilityId)
        ]);
        
        setSubscription(subscriptionData as unknown as MemberSubscription);
        setPaymentMethods(paymentMethodsResponse.data);
        setError(null);
      } else {
        setError(result.message || 'Error setting default payment method');
      }
    } catch (err: any) {
      setError(err.message || 'Error setting default payment method');
    } finally {
      setUpdatingPaymentMethod(null);
    }
  };

  const handleDetach = async (paymentMethodId: string) => {
    if (!currentUser) return;

    if (!businessId || !facilityId) {
      setError('No business or facility ID found');
      setLoading(false);
      return;
    }
    
    try {
      setRemovingPaymentMethod(paymentMethodId);
      const token = await currentUser.getIdToken();
      await memberApi.detachPaymentMethod(currentUser.uid, paymentMethodId, token, businessId, facilityId);
      
      // Refresh payment methods
      const response = await memberApi.getPaymentMethods(currentUser.uid, token, businessId, facilityId);
      setPaymentMethods(response.data);
    } catch (err: any) {
      setError(err.message || 'Error detaching payment method');
    } finally {
      setRemovingPaymentMethod(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700">
        No active subscription found. Please contact your facility administrator.
      </div>
    );
  }

  return (
    <div className="w-full pl-8 pr-4 py-8 flex flex-col h-full">
      <h1 className="text-2xl font-bold mb-6">My Subscription</h1>
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('subscription')}
            className={`${
              activeTab === 'subscription'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Subscription
          </button>
          <button
            onClick={() => setActiveTab('payment')}
            className={`${
              activeTab === 'payment'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Payment Methods
          </button>
        </nav>
      </div>

      {/* Subscription Tab */}
      {activeTab === 'subscription' && (
        <>  
          {/* Subscription Overview */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Subscription Details
              </h3>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <div className="text-sm font-medium text-gray-500">Status</div>
                  <div className="mt-1">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(subscription.status)}`}>
                      {subscription.status}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">Monthly Amount</div>
                  <div className="mt-1 text-sm text-gray-900">{formatCurrency(subscription.amount)}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">Next Billing Date</div>
                  <div className="mt-1 text-sm text-gray-900">{formatDate(subscription.currentPeriodEnd)}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">Start Date</div>
                  <div className="mt-1 text-sm text-gray-900">{formatDate(subscription.startDate)}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">Last Payment</div>
                  <div className="mt-1 text-sm text-gray-900">{formatDate(subscription.lastPaymentDate)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice History */}
          {invoices.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Billing History
                </h3>
                <div className="mt-5">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th scope="col" className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Invoice
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {invoices.map((invoice) => (
                          <tr key={invoice.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(invoice.date)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(invoice.amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(invoice.status)}`}>
                                {invoice.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <a href={invoice.pdfUrl} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-900">
                                Download
                              </a>
                              <span className="mx-2">•</span>
                              <a href={invoice.hostedUrl} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-900">
                                View
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Payment Methods Tab */}
      {activeTab === 'payment' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Payment Methods
            </h3>
            <div className="mt-5 space-y-4">
              {paymentMethods.length > 0 ? (
                paymentMethods.map((method) => (
                  <div 
                    key={method.id} 
                    className={`flex items-center justify-between p-4 rounded-lg ${
                      method.id === subscription.paymentMethod?.id 
                        ? 'bg-green-50 border-2 border-green-200' 
                        : 'bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900">
                            {method.card.brand.toUpperCase()} •••• {method.card.last4}
                          </p>
                          {method.id === subscription.paymentMethod?.id && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Current Billing
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {method.billing_details.name || 'No name provided'}
                        </p>
                        <p className="text-sm text-gray-500">
                          Expires {method.card.exp_month}/{method.card.exp_year}
                        </p>
                        {method.id === subscription.paymentMethod?.id && (
                          <p className="mt-1 text-sm font-medium text-green-600">
                            This card will be used for your next payment
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {method.id !== subscription.paymentMethod?.id && (
                        <button
                          onClick={() => handleSetDefault(method.id)}
                          disabled={updatingPaymentMethod === method.id}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {updatingPaymentMethod === method.id ? (
                            <div className="flex items-center">
                              <LoadingSpinner size="sm" className="mr-2" />
                              <span>Updating...</span>
                            </div>
                          ) : (
                            'Set as Current Billing'
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => handleDetach(method.id)}
                        disabled={paymentMethods.length === 1 || method.id === subscription.paymentMethod?.id || removingPaymentMethod === method.id}
                        className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded ${
                          paymentMethods.length === 1 || method.id === subscription.paymentMethod?.id || removingPaymentMethod === method.id
                            ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                            : 'text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                        }`}
                        title={
                          paymentMethods.length === 1
                            ? "You must have at least one payment method on file"
                            : method.id === subscription.paymentMethod?.id
                              ? "You cannot remove your current billing card. Set another card as current billing first."
                              : "Remove payment method"
                        }
                      >
                        {removingPaymentMethod === method.id ? (
                          <div className="flex items-center">
                            <LoadingSpinner size="sm" className="mr-2" />
                            <span>Removing...</span>
                          </div>
                        ) : (
                          'Remove'
                        )}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No payment methods found.</p>
              )}
            </div>

            <div className="mt-6">
              <button
                onClick={() => setActiveTab('setup')}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Add Payment Method
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Setup Tab */}
      {activeTab === 'setup' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            <PaymentSetup />
          </div>
        </div>
      )}
    </div>
  );
} 